import { ApolloError } from '@apollo/client';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { Banner, Button, theme } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import Icon from 'components/icons/Icon';
import PaymentCardDropDown from 'components/park/detail/PaymentCardDropDown';
import VehicleSelectDropDown from 'components/park/detail/VehicleSelectDropDown';
import { FooterButtonContainer } from 'components/sheets/SheetLayout';
import ParkingContext from 'context/ParkingContext';
import useReadPurchasedPrepaidTickets from 'hooks/useReadPurchasedPrepaidTickets';
import useSelectedParkingVehicle from 'hooks/useSelectedParkingVehicle';
import useUpdateParkingMutation from 'hooks/useUpdateParkingMutation';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  parkingPaymentCardId,
  parkingVehicleLicensePlate,
} from 'states/common';
import { ColumnWithMargins } from 'styles/ContainerStyles';
import {
  ParkingSession,
  PredefinedTicket,
  SelectedSegment,
  UpdateParkingSessionActionType,
} from 'types/generatedSchemaTypes';
import { ParkNavProps, ServicesStackParamList } from 'types/navigation';
import { isSalesOrderFree } from 'utils/commonUtils';
import Logger from 'utils/Logger';
import { isItemSalesOrder, showLicensePlate } from 'utils/permitUtils';

const WarningIcon = () => (
  <Icon name="Error-Outlined" color={theme.colors.black} />
);

const testID = 'prepaid-ticket-edit';

const EditPermitButton = () => {
  const { t } = useTranslation();
  const vehicle = useSelectedParkingVehicle();
  const {
    selectedParkzonePmcUnion,
    setSelectedParkzonePmcUnion,
    parkingZoneResult,
  } = useContext(ParkingContext);
  const { popTo: navigatePark } = useNavigation<ParkNavProps>();
  const {
    params: { ticketId },
  } = useRoute<RouteProp<ServicesStackParamList, 'prepaidEdit'>>();
  const { purchasedPrepaidTickets } = useReadPurchasedPrepaidTickets({
    fetchAll: true,
  });
  const isFreeSalesOrder =
    isItemSalesOrder(selectedParkzonePmcUnion) &&
    isSalesOrderFree(selectedParkzonePmcUnion.orderLines?.[0]?.pmc?.prices);

  const sessionId =
    (selectedParkzonePmcUnion as PredefinedTicket)?.sessionId ?? ticketId;

  const prepaidTicket = purchasedPrepaidTickets.find(
    (item) => item?.sessionId === sessionId
  );

  const customerSegment = {
    uid: prepaidTicket?.customerSegmentUid,
    countryCode: parkingZoneResult?.data?.parkZone?.countryCode,
  } as SelectedSegment;

  const navigateBack = () => {
    navigatePark('parkDetail', {
      parkingZoneUid: parkingZoneResult?.data?.parkZone?.uid || '',
    });
    setSelectedParkzonePmcUnion(undefined);
  };

  const getNotificationText = (reassignmentsLeft: number) => {
    let notificationText = t(
      'success.updatePrepaidTicket.reassignmentsSuccess'
    );
    if (reassignmentsLeft === 0) {
      notificationText += t(
        'success.updatePrepaidTicket.reassignmentsRestricted'
      );
    } else if (reassignmentsLeft) {
      notificationText += t('success.updatePrepaidTicket.reassignmentsLeft', {
        count: reassignmentsLeft,
      });
    }
    notificationText += ` ${t(
      'success.updatePrepaidTicket.reassignmentsInformation'
    )}`;
    return notificationText;
  };

  const callback = (
    _sessionId: string,
    _actionType: UpdateParkingSessionActionType,
    updatedPrepaidTicket: ParkingSession
  ) => {
    const reassignmentsLeft = updatedPrepaidTicket.reassignments
      ?.reassignmentsLeft as number;
    const text = getNotificationText(reassignmentsLeft);
    showToast(text, reassignmentsLeft <= 1 ? 'info' : 'success', 10000);
    parkingPaymentCardId(undefined);
    parkingVehicleLicensePlate(undefined);
    navigateBack();
  };

  const errorCallback = (error: ApolloError) => {
    if (
      error.graphQLErrors.some(
        (e) => e.extensions?.code === 'PREPAID_TICKET_DUPLICATED'
      )
    ) {
      showToast(
        `${t('parking.prepaidTickets.hasValidTicket', {
          zone: prepaidTicket?.parkZoneName,
          vehicle: vehicle?.licensePlate?.plateText,
        })}`,
        'info'
      );
      Logger.warn(
        `Prepaid ticket ${sessionId} cannot be updated because user has an active prepaid ticket for the vehicle ${vehicle?.licensePlate?.plateText} in the park zone ${prepaidTicket?.parkZoneName}.`
      );
    }
  };

  const { updateParking, updateParkingLoading } = useUpdateParkingMutation({
    sessionId: prepaidTicket?.sessionId as string,
    actionType: 'UPDATE',
    isModal: true,
    licensePlate: vehicle?.licensePlate?.plateText,
    callback,
    errorCallback,
    skipRefetch: true,
    customerSegment: customerSegment,
  });

  const updatePrepaidTicket = () => {
    updateParking();
  };

  const showReassignmentNotAllowed = useMemo(
    () => prepaidTicket?.reassignments?.reassignmentsLeft === 0,
    [prepaidTicket?.reassignments?.reassignmentsLeft]
  );

  const isPlateUnchanged =
    prepaidTicket?.accessDevice?.value === vehicle?.licensePlate?.plateText;

  const shouldDisable =
    (!vehicle || isPlateUnchanged) ??
    updateParkingLoading ??
    showReassignmentNotAllowed;

  return (
    <ColumnWithMargins>
      {showReassignmentNotAllowed && (
        <FooterButtonContainer pt={12} pb={0}>
          <Banner
            text={t('parking.prepaidTickets.editPrepaidRestricted', {
              count: prepaidTicket?.reassignments?.waitingHours as number,
            })}
            type="warning"
            iconComponent={WarningIcon}
          />
        </FooterButtonContainer>
      )}
      <FooterButtonContainer pb={0} prPl={10}>
        {showLicensePlate(selectedParkzonePmcUnion) && (
          <VehicleSelectDropDown isDisabled={showReassignmentNotAllowed} />
        )}
        {isItemSalesOrder(selectedParkzonePmcUnion) && (
          <PaymentCardDropDown isFreeSalesOrder={isFreeSalesOrder} />
        )}
      </FooterButtonContainer>
      <FooterButtonContainer>
        <Button
          loading={updateParkingLoading}
          disabled={shouldDisable}
          size="l"
          onPress={updatePrepaidTicket}
          text={t('button.save')}
          testID={`${testID}-save-changes-button`}
        />
      </FooterButtonContainer>
    </ColumnWithMargins>
  );
};

export default EditPermitButton;
